// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import Transition from 'presentations/views/components/atoms/Transition';

import styled from '@emotion/styled';
import { imagePath } from 'presentations/assets';
import { useRouter } from 'next/router';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import { gray04, white } from 'presentations/views/styles/color';
import Headline from 'presentations/views/components/atoms/Headline';
import { SyntheticEvent, useState } from 'react';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { ViewerItem } from 'presentations/api/schema/user';
import BackgroundContainer from 'presentations/views/components/organisms/service/BackgroundContainer';

interface Props {}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const localeText = router.locale === 'ja' ? 'ja' : 'en';

  const isMobile = isMediaMax('tablet');
  const [activeIndex, setActiveIndex] = useState<number>(1);
  const [isAutoLoop, setIsAutoLoop] = useState<boolean>(true);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);

  const onClickNavi = (e: SyntheticEvent<HTMLDivElement>) => {
    const { id } = e.currentTarget.dataset;
    onChangeTab(Number(id));
    setIsAutoLoop(false);
  };

  const onChangeTab = (index: number) => {
    setActiveIndex(index);
    const el = document.getElementsByClassName(
      `js-video-${index}`,
    )[0] as HTMLVideoElement;
    if (el) {
      el.currentTime = 0;
      el.play();
    }
  };

  const onVideoLoad = (e: SyntheticEvent<HTMLVideoElement>) => {
    const el = e.target as HTMLVideoElement;
    setIsVideoLoading(false);
  };

  const onVideoEnded = (e: SyntheticEvent<HTMLVideoElement>) => {
    if (!isAutoLoop) return;
    const el = e.target as HTMLVideoElement;
    const index = Number(el.dataset.id || 1);
    let nextIndex = index + 1;
    if (nextIndex > 3) nextIndex = 1;
    setTimeout(() => {
      onChangeTab(nextIndex);
    }, 2000);
  };

  return (
    <MainContainer>
      {!isMobile && (
        <NaviList>
          {[1, 2, 3].map((index) => (
            <NaviListItem
              key={index}
              isActive={activeIndex === index}
              onClick={onClickNavi}
              data-id={index}
            >
              <NaviListItemTitle>
                {t(`stepTitle.${index - 1}`)}
              </NaviListItemTitle>
              {activeIndex === index && (
                <NaviListItemText>
                  {t(`stepText.${index - 1}`)}
                </NaviListItemText>
              )}
            </NaviListItem>
          ))}
        </NaviList>
      )}
      {!isMobile ? (
        <VideoList>
          <HeaderImage
            width={840}
            height={36}
            src="pages/top/202306/header.svg"
            alt="Header"
          />
          {[1, 2, 3].map((index) => (
            <Transition
              key={index}
              name="fade"
              in={activeIndex === index}
              timeout={1000}
            >
              <VideoContainer isActive={activeIndex === index}>
                {isVideoLoading && (
                  <VideoLoading>
                    <Skeleton width={100} />
                  </VideoLoading>
                )}
                <VideoItem
                  data-id={index}
                  preload={index === 1 ? 'auto' : 'none'}
                  className={`js-video-${index}`}
                  src={imagePath(
                    `pages/top/202306/slide0${index}_${localeText}.mp4`,
                  )}
                  width={'100%'}
                  onEnded={onVideoEnded}
                  onPlay={onVideoLoad}
                  height={360}
                  autoPlay
                  muted
                />
              </VideoContainer>
            </Transition>
          ))}
        </VideoList>
      ) : (
        <>
          {[1, 2, 3].map((index) => (
            <StepContent>
              <VideoList>
                <HeaderImage
                  width={840}
                  height={36}
                  src="pages/top/202306/header.svg"
                  alt="Header"
                />
                <VideoContainer isActive>
                  <VideoItem
                    data-id={index}
                    preload="auto"
                    className={`js-video-${index}`}
                    src={imagePath(
                      `pages/top/202306/slide0${index}_${localeText}.mp4`,
                    )}
                    width={'100%'}
                    height={360}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                </VideoContainer>
              </VideoList>
              <StepContentTitle variant="h3">
                {t(`stepTitle.${index - 1}`)}
              </StepContentTitle>
              <StepContentText>{t(`stepText.${index - 1}`)}</StepContentText>
            </StepContent>
          ))}
        </>
      )}
    </MainContainer>
  );
}
const MainContainer = styled.div`
  display: flex;
  justify-content: space-between;

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const NaviList = styled.div`
  width: 420px;
  text-align: left;
  padding-left: ${spacing(32)};
  padding-top: ${spacing(24)};
`;
const NaviListItem = styled.div<{ isActive: boolean }>`
  margin-bottom: ${spacing(24)};
  padding: ${spacing(24)} ${spacing(64)};
  background-color: ${(props) => props.isActive && gray04};
  border-radius: 64px 0 0 64px;
  cursor: pointer;
  :last-of-type {
    margin-right: 0;
  }
`;

const NaviListItemTitle = styled.div`
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  margin-right: ${spacing(32)};
`;
const NaviListItemText = styled.div`
  margin-right: ${spacing(32)};
  margin-top: ${spacing(8)};
`;
const VideoList = styled.div`
  border-radius: ${borderRadius(12)} ${borderRadius(12)} 0 0;
  overflow: hidden;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  flex: 1;
  background-color: ${white};
  ${mediaMax.tablet} {
    background-color: transparent;
  }
`;
const VideoContainer = styled.div<{ isActive: boolean }>`
  display: ${(props) => (props.isActive ? 'block' : 'none')};
  height: 100%;
  overflow: hidden;
  position: relative;
`;
const VideoLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderImage = styled(Image)`
  width: 100%;
  height: auto;
  display: block;
`;

const VideoItem = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 ${spacing(12)} ${spacing(40)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const StepContentTitle = styled(Headline)`
  margin-top: ${spacing(32)};
  margin-bottom: ${spacing(16)};
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  text-align: left;
`;
const StepContentText = styled.div`
  margin-bottom: ${spacing(16)};
  text-align: left;
`;
